/* Phase 20 — Per-booking chat panel.
 * Shared component used in two places:
 *   - manager calendar booking-detail Sheet (sender_type=manager)
 *   - staff portal expanded job card (sender_type=staff)
 *
 * The differences are surface-only (endpoint paths + bubble alignment + label).
 * Polling every 10s while mounted. Mark-read PATCH fires on first load and
 * again whenever a new staff/manager message arrives (auto-keeps unread badge
 * accurate). Optimistic send: append a tmp bubble immediately, replace with
 * server response on success, drop on failure.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, Send, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { api as defaultApi, formatApiError } from "../lib/api";
import { toast } from "sonner";

const MAX_LEN = 1000;
const POLL_MS = 10000;

function relativeTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function MessageBubble({ msg, ownSide, testidPrefix }) {
  // ownSide=true → right-aligned primary; false → left-aligned slate.
  const isOwn = ownSide;
  return (
    <div
      className={`flex ${isOwn ? "justify-end" : "justify-start"} w-full`}
      data-testid={`${testidPrefix}-message-${msg.id}`}
    >
      <div className={`max-w-[80%] ${isOwn ? "items-end" : "items-start"} flex flex-col gap-0.5`}>
        <div
          className={`rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
            isOwn
              ? "bg-primary text-primary-foreground rounded-br-sm"
              : "bg-secondary text-secondary-foreground rounded-bl-sm"
          }`}
        >
          {msg.text}
        </div>
        <div className="text-[10px] text-muted-foreground mono px-1">
          {msg.sender_name} · {relativeTime(msg.created_at)}
        </div>
      </div>
    </div>
  );
}

export default function BookingChat({
  bookingId,
  side = "manager", // "manager" | "staff"
  label,            // optional header label override
  emptyHint,        // shown when feed is empty
  apiClient,        // axios instance: defaults to manager api; staff portal passes staffApi
}) {
  const api = apiClient || defaultApi;
  const isStaff = side === "staff";
  const basePath = isStaff
    ? `/staff-portal/bookings/${bookingId}/messages`
    : `/bookings/${bookingId}/messages`;
  const ownSideType = isStaff ? "staff" : "manager";
  const testidPrefix = isStaff ? "staff-chat" : "booking-chat";

  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const feedRef = useRef(null);
  const lastIsoRef = useRef(null);
  const pollRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
    });
  }, []);

  const markRead = useCallback(async () => {
    try { await api.patch(`${basePath}/mark-read`); } catch { /* best-effort */ }
  }, [basePath]);

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await api.get(basePath);
      setMessages(data || []);
      lastIsoRef.current = (data && data.length) ? data[data.length - 1].created_at : null;
      scrollToBottom();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load chat");
    } finally {
      setLoading(false);
    }
  }, [basePath, scrollToBottom]);

  const fetchDelta = useCallback(async () => {
    if (!lastIsoRef.current) return fetchAll();
    try {
      const { data } = await api.get(basePath, { params: { since: lastIsoRef.current } });
      if (data && data.length) {
        setMessages((prev) => [...prev, ...data]);
        lastIsoRef.current = data[data.length - 1].created_at;
        // If any incoming message is from the OTHER side, mark-read again so the
        // unread badge stays at zero while the panel is open.
        if (data.some((m) => m.sender_type !== ownSideType)) markRead();
        scrollToBottom();
      }
    } catch { /* swallow transient poll errors */ }
  }, [basePath, fetchAll, markRead, ownSideType, scrollToBottom]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      await fetchAll();
      if (!mounted) return;
      markRead();
    })();
    pollRef.current = setInterval(fetchDelta, POLL_MS);
    return () => {
      mounted = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  // bookingId is the only re-key trigger we need; helpers stable via useCallback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const send = async () => {
    const text = draft.trim();
    if (!text) return;
    if (text.length > MAX_LEN) {
      toast.error(`Message too long (max ${MAX_LEN} chars)`);
      return;
    }
    setSending(true);
    // Optimistic bubble — we mark it pending via a `_pending` flag.
    const tmpId = `tmp_${Date.now()}`;
    const optimistic = {
      id: tmpId,
      booking_id: bookingId,
      sender_type: ownSideType,
      sender_id: "me",
      sender_name: isStaff ? "You" : "You",
      text,
      created_at: new Date().toISOString(),
      _pending: true,
    };
    setMessages((p) => [...p, optimistic]);
    setDraft("");
    scrollToBottom();
    try {
      const { data } = await api.post(basePath, { text });
      setMessages((p) => p.map((m) => (m.id === tmpId ? data : m)));
      lastIsoRef.current = data.created_at;
    } catch (e) {
      setMessages((p) => p.filter((m) => m.id !== tmpId));
      toast.error(formatApiError(e?.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="pt-4 mt-4 border-t border-border" data-testid={`${testidPrefix}-panel`}>
      <div className="flex items-center gap-2 mb-2">
        <MessageCircle className="h-4 w-4 text-accent" />
        <div className="text-sm font-bold">{label || "Chat"}</div>
      </div>

      <div
        ref={feedRef}
        className="bg-muted/40 rounded-md p-3 space-y-2 max-h-[320px] overflow-y-auto"
        data-testid={`${testidPrefix}-feed`}
      >
        {loading && (
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading…
          </div>
        )}
        {!loading && messages.length === 0 && (
          <div className="text-xs text-muted-foreground italic" data-testid={`${testidPrefix}-empty`}>
            {emptyHint || "No messages yet. Say hi 👋"}
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble
            key={m.id}
            msg={m}
            ownSide={m.sender_type === ownSideType}
            testidPrefix={testidPrefix}
          />
        ))}
      </div>

      <div className="mt-3 flex items-end gap-2">
        <div className="flex-1">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, MAX_LEN))}
            onKeyDown={onKeyDown}
            placeholder="Type a message…"
            rows={2}
            className="resize-none min-h-[44px] text-sm"
            data-testid={`${testidPrefix}-composer`}
          />
          <div className="flex justify-end text-[10px] text-muted-foreground mono mt-1">
            {draft.length}/{MAX_LEN}
          </div>
        </div>
        <Button
          onClick={send}
          disabled={sending || !draft.trim()}
          className="min-h-[44px] min-w-[44px] px-3"
          data-testid={`${testidPrefix}-send`}
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </div>
  );
}
